import './add-material-modal.css';

import { BaseSyntheticEvent, ReactNode, useContext, useState } from 'react';
import { DefaultValues, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';

import ModalContext from '../../../../contexts/ModalContext';
import { TokenAccessor } from '@pelckmans/business-components/types/API';
import { UserMaterial } from '../../../../api/user-material';
import { BasePayload, editOrSaveUserMaterial } from './api';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import useAnalytics from '../../../../hooks/useAnalytics';

export interface BaseFormValues {
  name: string;
}

export interface RenderProps<T extends BaseFormValues> extends UseFormReturn<T> {
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
}

export interface BaseAddMaterialModalBaseProps {
  tokenAccessor: TokenAccessor;
  moduleId: string;
  nodeId: string;
  studioGroup: string;
  onSubmit: (userMaterial?: UserMaterial) => void;
}

interface Props<T extends BaseFormValues> extends BaseAddMaterialModalBaseProps {
  modalId: string;
  formId: string;
  children: (props: RenderProps<T>) => ReactNode;
  defaultValues: DefaultValues<T>;
  payloadMapper: (data: T) => BasePayload | null;
  materialId?: string;
}

const BASE = 'SHARED_COMPONENTS.ADD_MATERIAL_MODAL';

export default function BaseAddMaterialModal<T extends BaseFormValues>({
  modalId,
  formId,
  children,
  tokenAccessor,
  defaultValues,
  moduleId,
  nodeId,
  studioGroup,
  onSubmit: onSubmitCallback,
  payloadMapper,
  materialId,
}: Props<T>) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const analytics = useAnalytics();

  const [showAlert, setShowAlert] = useState<'retry' | 'invalid' | null>(null);

  function handleClose(disableAnimation: boolean = false) {
    closeModal(modalId, disableAnimation);
  }

  const formData = useForm<T>({
    defaultValues,
    mode: 'onBlur',
  });

  const { isSubmitting } = formData.formState;

  const onFormSubmit =
    (showShare: boolean = false) =>
    async (data: T) => {
      try {
        const payload = payloadMapper(data);
        if (!payload) {
          return handleClose();
        }

        const material = await editOrSaveUserMaterial({
          materialId,
          moduleId,
          nodeId,
          payload,
          tokenAccessor,
          studioGroup,
        });

        if (!materialId) {
          analytics.capture({
            eventType: ANALYTICS_EVENT_TYPES.USER_MATERIAL_CREATED,
            objectId: material?.id,
            subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
          });
        }

        handleClose(true);
        return onSubmitCallback(showShare ? material : undefined);
      } catch (e) {
        setShowAlert('retry');
      }
    };

  function handleInvalidSubmit() {
    setShowAlert('invalid');
  }

  function renderAlert() {
    if (showAlert === 'retry') {
      return (
        <Alert
          prominence="danger"
          actions={{
            'aria-label': undefined,
            size: 'sm',
            role: undefined,
            children: (
              <Button
                prominence="danger"
                onClick={formData.handleSubmit(onFormSubmit(), handleInvalidSubmit)}
                busy={isSubmitting}
              >
                {t(`${BASE}.ALERTS.RETRY.ACTION`)}
              </Button>
            ),
          }}
        >
          {t(`${BASE}.ALERTS.RETRY.MESSAGE`)}
        </Alert>
      );
    }
    if (showAlert === 'invalid') {
      return <Alert prominence="danger">{t(`${BASE}.ALERTS.INVALID.MESSAGE`)}</Alert>;
    }

    return null;
  }

  return (
    <Dialog
      aria-label={t(`${BASE}.A11Y_LABEL${materialId ? '_UPDATE' : ''}`)}
      id={modalId}
      modal
      size="sm"
      onClose={() => handleClose()}
    >
      <Dialog.Header
        title={t(`${BASE}.TITLE${materialId ? '_UPDATE' : ''}`)}
        onClose={() => handleClose()}
        aria-label={t(`${BASE}.ACTIONS.CLOSE`)}
      />
      <Dialog.Body
        alertGroupProps={
          !showAlert ? undefined : { 'aria-label': undefined, role: undefined, children: renderAlert()! }
        }
      >
        {children({
          ...formData,
          onSubmit: formData.handleSubmit(onFormSubmit(), handleInvalidSubmit),
        })}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: 'group',
          children: (
            <>
              <Button key="cancel" onClick={() => handleClose()} prominence="neutral">
                {t(`${BASE}.ACTIONS.CANCEL`)}
              </Button>
              {!materialId ? (
                <Button
                  key="addAndShare"
                  onClick={formData.handleSubmit(onFormSubmit(true), handleInvalidSubmit)}
                  prominence="success"
                  busy={isSubmitting}
                >
                  {t(`${BASE}.ACTIONS.ADD_AND_SHARE`)}
                </Button>
              ) : null}
              <Button key="add" prominence="success" type="submit" form={formId} busy={isSubmitting}>
                {materialId ? t(`${BASE}.ACTIONS.UPDATE`) : t(`${BASE}.ACTIONS.ADD`)}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}
